import {
  ButtonBase,
  Dialog,
  DialogActions,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { Done, HighlightOff } from '@material-ui/icons';
import CloseIcon from '@material-ui/icons/Close';
import { modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import Button from 'components/Button';
import ApplicationState from 'store/interfaces';
import clsx from 'clsx';
import { useRunOnReload } from 'hooks';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import i18n from 'i18n';
import { inSuperAdminView } from 'utils/role';
import styles from './styles';

interface Props {
  companySelected: CompanyData | null;
  modal: ModalFields | null;
  role?: RolesEnum;
  modalClose: () => void;
}

const Modal = ({ companySelected, modal, role, modalClose }: Props) => {
  const useStyles = makeStyles(styles(modal?.overrideClasses));

  const classes = useStyles({});
  useRunOnReload({
    listener: () => modalClose(),
  });

  const handleCloseModal = () => {
    if (modal?.onClose) {
      modal.onClose();
    }
    modalClose();
  };

  return (
    <React.Fragment>
      <Dialog
        scroll="body"
        classes={{ scrollBody: classes.dialogScrollBody }}
        onClose={handleCloseModal}
        open={!!modal?.content}
        maxWidth={modal?.maxWidth}
        fullWidth={modal?.fullWidth}
        disableBackdropClick={modal?.disableBackdropClick}
        disableEscapeKeyDown={modal?.disableEscapeKeyDown}
      >
        {modal?.response ? (
          <>
            <div className={classes.responseIcon}>
              {modal?.sucess ? (
                <Done className={classes.buttonDone} />
              ) : (
                <HighlightOff />
              )}
            </div>
            <header className={classes.headerResponse}>
              <span>
                <Grid container className={classes.header}>
                  <Typography className={classes.titleResponse}>
                    {modal?.title}
                  </Typography>
                </Grid>
                {modal?.subtitle && (
                  <Typography className={classes.subTitleResponse} variant="h6">
                    {modal?.subtitle}
                  </Typography>
                )}
                {modal?.information && (
                  <Typography
                    className={classes.subTitleInformation}
                    variant="h6"
                  >
                    {modal?.information}
                  </Typography>
                )}
              </span>

              {modal?.action}
            </header>
          </>
        ) : (
          <header
            className={clsx(classes.header, {
              [classes.headerSuperAdmin]:
                !companySelected && role === RolesEnum.superAdmin,
              [classes.headerAdmin]:
                !companySelected && role === RolesEnum.admin,
              [classes.headerOperator]:
                !companySelected && role === RolesEnum.operator,
            })}
          >
            <span>
              {modal?.erroModal ? (
                <Grid className={classes.errorModalSuperAdmin}>
                  <HighlightOff />
                  <Typography
                    className={clsx(classes.title, {
                      [classes.titleSuperAdmin]: inSuperAdminView(
                        role,
                        companySelected
                      ),
                    })}
                  >
                    {modal?.title}
                  </Typography>
                </Grid>
              ) : (
                <Typography className={classes.errorModalSuperAdmin}>
                  {modal?.title}
                </Typography>
              )}
              {modal?.subtitle && (
                <Typography
                  className={clsx(classes.subtitle, {
                    [classes.subtitleSuperAdmin]: inSuperAdminView(
                      role,
                      companySelected
                    ),
                  })}
                >
                  {modal?.subtitle}
                </Typography>
              )}
              {modal?.information && (
                <Typography
                  className={classes.subTitleInformation}
                  variant="h6"
                >
                  {modal?.information}
                </Typography>
              )}
            </span>
            {modal?.action || (
              <IconButton
                onClick={handleCloseModal}
                className={clsx(classes.closeButton, {
                  [classes.closeButtonSuperAdmin]: inSuperAdminView(
                    role,
                    companySelected
                  ),
                })}
              >
                <CloseIcon />
              </IconButton>
            )}
          </header>
        )}

        {modal?.response ? (
          <div className={classes.contentResponse}>
            <Typography
              variant="h6"
              color="textSecondary"
              className={classes.responseContentText}
            >
              {modal?.content}
            </Typography>
          </div>
        ) : (
          <div
            className={clsx(classes.content, {
              [classes.contentNoBorder]: modal?.noBorder,
            })}
          >
            <Typography className={classes.contentText}>
              {modal?.content}
            </Typography>
          </div>
        )}

        {modal?.response && (
          <div className={classes.buttonContainer}>
            <ButtonBase id="close-modal" onClick={modalClose}>
              <Typography className={classes.buttonClose} variant="h6">
                {i18n.t('CLOSE_KEY')}
              </Typography>
            </ButtonBase>
          </div>
        )}

        {modal?.triggerFunction && (
          <div className={classes.buttonContainer}>
            <Button id="close-modal" onClick={modal.triggerFunction.action}>
              <Typography className={classes.buttonClose} variant="h6">
                {modal.triggerFunction.label}
              </Typography>
            </Button>
          </div>
        )}

        {modal?.footer && (
          <Grid container className={classes.footerItems}>
            <Grid container className={classes.dialogActions}>
              <DialogActions>{modal.footer}</DialogActions>
            </Grid>
          </Grid>
        )}

        {!!modal?.genericFooter && (
          <Grid container className={classes.footerItems}>
            <Grid container className={classes.dialogActions}>
              <DialogActions>
                <Grid item container justify="flex-end">
                  <Button
                    variant="outlined"
                    onClick={modal?.genericFooter.action || handleCloseModal}
                  >
                    {modal?.genericFooter.label || i18n.t('CLOSE_KEY')}
                  </Button>
                </Grid>
              </DialogActions>
            </Grid>
          </Grid>
        )}
      </Dialog>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  modal: { modalDetails },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  modal: modalDetails,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, { modalClose })(Modal);
